import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #333;
  margin-bottom: 2rem;
`;

const ImageList = styled.div`
  display: grid;
  gap: 2rem;
`;

const ImageCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || '1fr 1fr'};
  gap: 1rem;
  margin: 1rem 0;
`;

const ImageContainer = styled.div`
  position: relative;
  cursor: pointer;

  &:hover .download-icon {
    opacity: 1;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 4px;
`;

const DownloadIcon = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px;
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => props.status === 'pending' ? '#fff3e0' : '#e8f5e9'};
  color: ${props => props.status === 'pending' ? '#e65100' : '#2e7d32'};
`;

const MetaData = styled.div`
  margin-top: 1rem;
  font-size: 0.875rem;
  color: #666;

  p {
    margin: 0.5rem 0;
  }
`;

const PromptText = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f5f5f5;
  border-radius: 4px;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #333;
  border-left: 3px solid #007bff;
`;

const DeleteButton = styled.button`
  background-color: #f44336;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 1rem;

  &:hover {
    background-color: #d32f2f;
  }

  &:disabled {
    background-color: #e0e0e0;
    cursor: not-allowed;
  }
`;

function AdminPage() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const navigate = useNavigate();

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    setDeleteLoading(id);
    const sessionToken = localStorage.getItem('sessionToken');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/images?id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete image');
      }

      // Remove the deleted image from the state
      setImages(images.filter(image => image.id !== id));
    } catch (err) {
      alert(`Error: ${err.message}`);
      if (err.message === 'Unauthorized') {
        navigate('/');
      }
    } finally {
      setDeleteLoading(null);
    }
  };


  useEffect(() => {
    const sessionToken = localStorage.getItem('sessionToken');
    if (!sessionToken) {
      navigate('/');
      return;
    }

    fetch(`${process.env.REACT_APP_API_URL}/admin/images`, {
      headers: {
        'Authorization': `Bearer ${sessionToken}`
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Unauthorized');
        }
        return response.json();
      })
      .then(data => {
        // Parse the nested user_email JSON string in each image
        const parsedImages = data.images.map(item => ({
          ...item,
          user_email: JSON.parse(item.user_email).email
        }));
        setImages(parsedImages);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
        if (err.message === 'Unauthorized') {
          navigate('/');
        }
      });
  }, [navigate]);

  if (loading) {
    return (
      <Container>
        <Title>Loading...</Title>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Title>Error</Title>
        <p>{error}</p>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Admin Dashboard</Title>
      <ImageList>
        {images.map(image => (
          <ImageCard key={image.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Request from {image.user_email}</h3>
              <StatusBadge status={image.status}>{image.status}</StatusBadge>
            </div>
            <ImageGrid columns={image.combined_image_url ? '1fr 1fr 1fr' : '1fr 1fr'}>
              <div>
                <h4>Home Image</h4>
                <a href={image.home_image_url} download style={{ textDecoration: 'none' }}>
                  <ImageContainer>
                    <Image src={image.home_image_url} alt="Home" />
                    <DownloadIcon className="download-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor"/>
                      </svg>
                    </DownloadIcon>
                  </ImageContainer>
                </a>
              </div>
              <div>
                <h4>Inspiration Image</h4>
                <a href={image.inspiration_image_url} download style={{ textDecoration: 'none' }}>
                  <ImageContainer>
                    <Image src={image.inspiration_image_url} alt="Inspiration" />
                    <DownloadIcon className="download-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor"/>
                      </svg>
                    </DownloadIcon>
                  </ImageContainer>
                </a>
              </div>
              {image.combined_image_url && (
                <div>
                  <h4>Combined Image</h4>
                  <a href={image.combined_image_url} download style={{ textDecoration: 'none' }}>
                    <ImageContainer>
                      <Image src={image.combined_image_url} alt="Combined" />
                      <DownloadIcon className="download-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor"/>
                        </svg>
                      </DownloadIcon>
                    </ImageContainer>
                  </a>
                </div>
              )}
            </ImageGrid>
            <MetaData>
              <p>Created: {new Date(image.created_at).toLocaleString()}</p>
              <p>Last Updated: {new Date(image.updated_at).toLocaleString()}</p>
              {image.prompt_text && (
                <PromptText>
                  <strong>Prompt:</strong> {image.prompt_text}
                </PromptText>
              )}
              <DeleteButton 
                onClick={() => handleDelete(image.id)} 
                disabled={deleteLoading === image.id}
              >
                {deleteLoading === image.id ? 'Deleting...' : 'Delete Image'}
              </DeleteButton>
            </MetaData>
          </ImageCard>
        ))}
      </ImageList>
    </Container>
  );
}

export default AdminPage;
